import type { FC, PropsWithChildren } from 'react';

import styled, { css } from 'styled-components';

import { DEFAULT_SIZES } from './constants';
import { useTileImageContext } from './TileImageContext';
import { ImageWithLoaderIndicator } from '../ImageWithLoaderIndicator';
import { AiWatermark } from '~/components/shared/aiWatermark/AiWatermark';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';

const S = {
  ProductImageWrapper: styled.div<{ $aspectRatio?: number }>`
    --ai-icon-z-index: auto;

    ${({ $aspectRatio, theme }) => css`
      --aspect-ratio: ${$aspectRatio ?? 0.75};

      background-color: ${theme.colors.neutral[200]};
      position: relative;
      width: 100%;
      height: 100%;
      aspect-ratio: var(--aspect-ratio);

      @supports not (aspect-ratio: var(--aspect-ratio)) {
        height: 0;
        padding-bottom: 135%;

        > * {
          position: absolute;
          inset: 0;
          height: 100%;
        }
      }
    `}
  `,

  HoveredImage: styled(ImageWithLoaderIndicator)`
    background-color: transparent;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  `,
};

interface TileImageDisplayProps {
  showHover: boolean;
  alt: string;
  index?: number;
}

export const TileImageDisplay: FC<PropsWithChildren<TileImageDisplayProps>> = ({
  index,
  showHover,
  alt = '',
  children,
}) => {
  const { aspectRatio } = useTileContext();
  const { primaryImage, hoverImage } = useTileImageContext();

  if (!primaryImage) {
    return <S.ProductImageWrapper />;
  }

  const loading = index !== undefined && index < 8 ? 'eager' : 'lazy';
  const activeImage = showHover && hoverImage ? hoverImage : primaryImage;

  return (
    <S.ProductImageWrapper $aspectRatio={aspectRatio}>
      {children}
      <ImageWithLoaderIndicator
        src={primaryImage.src}
        alt={alt}
        loading={loading}
        srcSet={primaryImage.srcSet}
        sizes={DEFAULT_SIZES}
        fetchPriority="high"
      />

      {showHover && hoverImage && (
        <S.HoveredImage
          src={hoverImage.src}
          alt={alt}
          loading="eager"
          srcSet={hoverImage.srcSet}
          sizes={DEFAULT_SIZES}
          shimmerDuration="0s"
          fadeInOnLoad={false}
        />
      )}
      <AiWatermark show={activeImage?.isAI} className="ai-icon-container" />
    </S.ProductImageWrapper>
  );
};
